// MeetingRow — Presentational single-meeting card for the EditTripModal's
// Business Meetings list: the bordered white row showing the meeting title +
// company name, the formatted meeting time (Calendar icon), the address rendered
// as a Google Maps link (MapPin icon), the optional italic notes, and the
// Edit / Delete action buttons.
//
// Extracted verbatim from `EditTripModal.js` (the body of the
// `editedTrip.meetings.map(...)` callback) as part of the behavior-preserving
// refactor (issue #240). The display helpers `formatMeetingTime` and
// `generateGoogleMapsUrl` are imported from `../../../types/Meeting` exactly as
// the source used them. In the source the Edit button called
// `setEditingMeeting(meeting)` and the Delete button called
// `handleDeleteMeeting(meeting.id)`; here those are surfaced as the `onEdit` and
// `onDelete` callbacks so the component stays render-only. Wrapped in React.memo.
// The address link's `target`/`rel`, the inline hover handlers, `title`
// tooltips, the `meeting.notes` render guard, icons (lucide-react), and styles
// are identical to the source.
//
// Props:
//   meeting  - the meeting object ({ meetingTitle, companyName, address, notes, ... })
//   onEdit   - () => void invoked by the "Edit meeting" button
//   onDelete - () => void invoked by the "Delete meeting" button
//
// Validates: Requirements 3.3, 3.4, 1.2, 1.6

import React from 'react';
import { Edit3, Trash2, Building2, Calendar, MapPin } from 'lucide-react';
import { formatMeetingTime, generateGoogleMapsUrl } from '../../../types/Meeting';

export const MeetingRow = React.memo(function MeetingRow({
  meeting,
  onEdit,
  onDelete
}) {
  return (
    <div style={{
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      padding: '16px',
      backgroundColor: '#ffffff',
      transition: 'all 0.2s'
    }}
    onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
    onMouseLeave={(e) => e.target.style.backgroundColor = '#ffffff'}
    >
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between'
      }}>
        <div style={{ flex: 1 }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '8px'
          }}>
            <Building2 size={16} style={{ color: '#7c3aed' }} />
            <span style={{ fontWeight: '600', color: '#1f2937' }}>{meeting.meetingTitle}</span>
            <span style={{ fontSize: '14px', color: '#6b7280' }}>•</span>
            <span style={{ fontSize: '14px', color: '#6b7280' }}>{meeting.companyName}</span>
          </div>
          
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            fontSize: '14px',
            color: '#6b7280',
            marginBottom: '8px'
          }}>
            <Calendar size={12} />
{formatMeetingTime(meeting)}
          </div>
          
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '6px',
            fontSize: '14px',
            color: '#6b7280'
          }}>
            <MapPin size={12} style={{ marginTop: '2px', color: '#9ca3af' }} />
            <div style={{ flex: 1 }}>
              <a
                href={generateGoogleMapsUrl(meeting.address)}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#1e40af',
                  textDecoration: 'none',
                  borderBottom: '1px solid #1e40af'
                }}
                onMouseEnter={(e) => e.target.style.color = '#dc2626'}
                onMouseLeave={(e) => e.target.style.color = '#1e40af'}
              >
                {meeting.address}
              </a>
            </div>
          </div>
          
          {meeting.notes && (
            <div style={{
              fontSize: '12px',
              color: '#6b7280',
              fontStyle: 'italic',
              marginTop: '8px'
            }}>
              {meeting.notes}
            </div>
          )}
        </div>
        
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          marginLeft: '16px'
        }}>
          <button
            onClick={() => onEdit()}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#9ca3af',
              padding: '4px',
              borderRadius: '4px',
              transition: 'color 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.color = '#3b82f6'}
            onMouseLeave={(e) => e.target.style.color = '#9ca3af'}
            title="Edit meeting"
          >
            <Edit3 size={16} />
          </button>
          <button
            onClick={() => onDelete()}
            style={{
              background: 'none',
              border: '1px solid transparent',
              cursor: 'pointer',
              color: '#9ca3af',
              padding: '6px',
              borderRadius: '6px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.target.style.color = '#ef4444';
              e.target.style.backgroundColor = '#fef2f2';
              e.target.style.borderColor = '#fecaca';
            }}
            onMouseLeave={(e) => {
              e.target.style.color = '#9ca3af';
              e.target.style.backgroundColor = 'transparent';
              e.target.style.borderColor = 'transparent';
            }}
            title="Delete meeting"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>
    </div>
  );
});

export default MeetingRow;
